/**
 * FixtureResetCard.js — Fix Round v7 (2026-07-12).
 *
 * Super-admin card on /admin/diagnostics that wipes the fixture project's
 * drawings + issue sets + transmittals in one click. Requires typing
 * "RESET FIXTURE" to confirm. Displays per-entity delete counts on
 * completion.
 *
 * Backend endpoint: POST /api/admin/fixture-reset
 * Scope hard-coded backend-side to project 7a0a0894-...c387d in company
 * 31037fd8-...25ab. This UI cannot request any other scope.
 */
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, AlertTriangle, CheckCircle2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';
const CONFIRM_WORD = 'RESET FIXTURE';

export default function FixtureResetCard({ isSuperAdmin }) {
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  if (!isSuperAdmin) return null;

  const canRun = confirmText === CONFIRM_WORD && !busy;

  const onRun = async () => {
    setBusy(true); setError(null); setResult(null);
    try {
      const tok = localStorage.getItem('accessToken') || localStorage.getItem('token');
      const r = await fetch(`${API}/api/admin/fixture-reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${tok}`,
        },
        body: JSON.stringify({ confirm: CONFIRM_WORD }),
      });
      const body = await r.json().catch(() => null);
      if (!r.ok) {
        setError(body?.detail || `HTTP ${r.status}`);
      } else {
        setResult(body);
        setConfirmText('');
      }
    } catch (e) {
      setError(e?.message || String(e));
    } finally { setBusy(false); }
  };

  return (
    <div
      className="p-5 rounded-xl border border-red-200 bg-red-50/40 mb-6"
      data-testid="fixture-reset-card"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-6 h-6 text-red-600 mt-0.5 shrink-0" />
        <div className="flex-1">
          <h3 className="text-lg font-bold text-red-900" data-testid="fixture-reset-title">
            Reset Fixture Project
          </h3>
          <p className="text-sm text-slate-700 mt-1">
            Wipes <b>drawings, issue sets, and transmittals</b> for the diagnostic
            fixture project only (project id <code className="bg-white px-1 py-0.5 rounded text-xs">7a0a0894-…c387d</code>,
            company <code className="bg-white px-1 py-0.5 rounded text-xs">31037fd8-…25ab</code>).
            No other project, no photos, no punch data. Type <b>{CONFIRM_WORD}</b> to enable.
          </p>
          <div className="mt-3 flex gap-2 items-center">
            <Input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={`Type ${CONFIRM_WORD} to enable`}
              className="max-w-xs"
              data-testid="fixture-reset-confirm-input"
            />
            <Button
              variant="destructive"
              onClick={onRun}
              disabled={!canRun}
              data-testid="fixture-reset-run-btn"
            >
              {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Deleting…</> : 'Reset Fixture'}
            </Button>
          </div>
          {error && (
            <div className="mt-3 p-2 rounded bg-red-100 border border-red-300 text-sm text-red-900" data-testid="fixture-reset-error">
              {error}
            </div>
          )}
          {result && (
            <div className="mt-3 p-3 rounded bg-emerald-50 border border-emerald-300" data-testid="fixture-reset-result">
              <div className="flex items-center gap-2 text-emerald-900 font-semibold">
                <CheckCircle2 className="w-5 h-5" /> Reset complete
              </div>
              <ul className="mt-2 text-sm text-slate-800 space-y-1 font-mono">
                {/* v7.2 (2026-07-12) — render every key the backend
                    actually returns (drawings, drawing_sets,
                    transmittals, unfiled_items) instead of the stale
                    three-label list. */}
                {Object.entries(result.deleted || {}).map(([k, v]) => (
                  <li key={k} data-testid={`fixture-reset-count-${k}`}>
                    {k.replace(/_/g, ' ')} deleted: <b>{v}</b>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
